import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  stacked?: boolean;
}

export function Modal({ open, onClose, title, children, footer, stacked }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(open);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (stacked) e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKey, stacked);
    return () => window.removeEventListener('keydown', handleKey, stacked);
  }, [open, onClose, stacked]);

  if (!open) return null;

  return createPortal(
    <div
      className={`fixed inset-0 ${stacked ? 'z-[65]' : 'z-sheet'} bg-black/50 flex items-end sm:items-center justify-center sm:p-4 cursor-auto select-text`}
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      onMouseDown={stopPropagation}
      onMouseMove={stopPropagation}
      onMouseUp={stopPropagation}
      onTouchStart={stopPropagation}
      onTouchEnd={stopPropagation}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-lg w-full sm:max-w-lg max-h-[90dvh] sm:max-h-[85vh] flex flex-col overflow-hidden focus:outline-none"
      >
        <div className="flex items-center justify-between gap-4 pl-5 pr-2 py-2 border-b border-champagne bg-white shrink-0 min-h-[3.5rem]">
          {title ? (
            <h2 id={titleId} className="text-base font-semibold text-text-dark">
              {title}
            </h2>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="w-11 h-11 rounded-full flex items-center justify-center text-text-medium hover:text-chocolate-dark hover:bg-champagne-light transition-colors shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto overscroll-contain p-5 ${footer ? '' : 'pb-safe sm:pb-5'}`}>{children}</div>
        {footer && <div className="flex-shrink-0 border-t border-champagne px-5 pt-4 pb-safe sm:pb-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
